// src/pages/AdminUsuarios.jsx
import React from 'react';
import {
  Box, Paper, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, FormControlLabel, Switch, Checkbox, Snackbar, Alert,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Key as KeyIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

/* ============================
   ENV / API CLIENT (CRA)
   ============================ */
const API_BASE = process.env.REACT_APP_API_BASE ?? '';
console.log('🔍 AdminUsuarios - API_BASE (env):', process.env.REACT_APP_API_BASE);
console.log('🔍 AdminUsuarios - API_BASE (final):', API_BASE);

const API_KEY = process.env.REACT_APP_API_KEY || '';
const DEFAULT_HEADERS = API_KEY
  ? { 'Content-Type': 'application/json', 'x-api-key': API_KEY }
  : { 'Content-Type': 'application/json' };

/* ============================
   Constantes de domínio
   ============================ */
const ROLES = ['MASTER', 'SECTOR'];
const SECTORS = ['Financeiro', 'Tráfego', 'Compras', 'TI', 'Manutenção', 'Marketing', 'Jurídico', 'all'];

/** Abas disponíveis para liberar por usuário
 *  IDs devem bater com os usados nas proteções de rota (canView/ensureTab).
 */
const ALL_TABS = [
  { id: 'operacao',    label: 'Operação (Tráfego)' },
  { id: 'penalizacao', label: 'Penalizações' },
  { id: 'setor2',      label: 'Setor 2 (KPI)' },
  { id: 'setor3',      label: 'Manutenção' },
  { id: 'setor4',      label: 'Setor 4 (Arrecadação)' },
  { id: 'setor5',      label: 'Setor 5' },
  { id: 'setor6',      label: 'Setor 6' },
  { id: 'setor7',      label: 'Setor 7' },
];

/* ============================
   Snackbar helper
   ============================ */
function useSnack() {
  const [snack, setSnack] = React.useState({ open: false, msg: '', sev: 'success' });
  const show = (msg, sev = 'success') => setSnack({ open: true, msg, sev });
  const node = (
    <Snackbar
      open={snack.open}
      autoHideDuration={3000}
      onClose={() => setSnack(s => ({ ...s, open: false }))}
    >
      <Alert
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        severity={snack.sev}
        variant="filled"
      >
        {snack.msg}
      </Alert>
    </Snackbar>
  );
  return { show, node };
}

/* ============================
   Página
   ============================ */
export default function AdminUsuarios() {
  const me = React.useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const onlyViewer = !(me?.canManageUsers || me?.username === 'Joziel'); // segurança visual (o back já protege)

  const { show, node } = useSnack();
  const [loading, setLoading] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [search, setSearch] = React.useState('');

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [form, setForm] = React.useState({
    username: '', displayName: '', role: 'SECTOR', allowedSector: 'Financeiro',
    password: '', canManageUsers: false, allowedTabs: []
  });

  const [pwOpen, setPwOpen] = React.useState(false);
  const [pwUser, setPwUser] = React.useState(null);
  const [newPassword, setNewPassword] = React.useState('');

  const navigate = useNavigate();
  const handleGoBack = () => navigate('/'); // volta para a tela de Login

  /* ============================
     Data fetching
     ============================ */
  const fetchUsers = React.useCallback(async (term = '') => {
    setLoading(true);
    try {
      const url = `${API_BASE}/api/admin/users${term ? `?search=${encodeURIComponent(term)}` : ''}`;
      const res = await fetch(url, { credentials: 'include', headers: DEFAULT_HEADERS });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error || res.statusText || 'Falha ao carregar');
      }
      setUsers(Array.isArray(data) ? data : (data.users || []));
    } catch (e) {
      show(`Erro ao listar: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [show]);

  React.useEffect(() => {
    fetchUsers('');
  }, [fetchUsers]);

  React.useEffect(() => {
    const t = setTimeout(() => fetchUsers(search), 500);
    return () => clearTimeout(t);
  }, [search, fetchUsers]);

  function parseTabs(v) {
    if (Array.isArray(v)) return v;
    return String(v || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  function openCreate() {
    setEditId(null);
    setForm({
      username: '', displayName: '', role: 'SECTOR', allowedSector: 'Financeiro',
      password: '', canManageUsers: false, allowedTabs: []
    });
    setDialogOpen(true);
  }

  function openEdit(u) {
    setEditId(u.id);
    setForm({
      username: u.username,
      displayName: u.display_name || u.displayName || '',
      role: u.role,
      allowedSector: u.allowed_sector || u.allowedSector,
      password: '',
      canManageUsers: !!(u.can_manage_users || u.canManageUsers),
      // aceita snake/camel por compatibilidade
      allowedTabs: parseTabs(u.allowed_tabs ?? u.allowedTabs)
    });
    setDialogOpen(true);
  }

  async function saveUser() {
    try {
      if (editId === null) {
        // criar
        const body = {
          username: form.username.trim(),
          displayName: form.displayName.trim(),
          role: form.role,
          allowedSector: form.role === 'MASTER' ? 'all' : form.allowedSector,
          password: form.password,
          canManageUsers: form.canManageUsers,
          allowedTabs: Array.isArray(form.allowedTabs) ? form.allowedTabs : []
        };
        if (!body.username || !body.displayName || !body.password) {
          return show('Preencha usuário, nome e senha.', 'warning');
        }
        const res = await fetch(`${API_BASE}/api/admin/users`, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          credentials: 'include',
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.error || res.statusText);
        show('Usuário criado.');
      } else {
        // editar (não troca senha aqui)
        const body = {
          displayName: form.displayName.trim(),
          role: form.role,
          allowedSector: form.role === 'MASTER' ? 'all' : form.allowedSector,
          allowedTabs: Array.isArray(form.allowedTabs) ? form.allowedTabs : []
        };
        // só Joziel pode marcar essa chavinha (o back valida de novo)
        if (me?.username === 'Joziel') body.canManageUsers = !!form.canManageUsers;

        const res = await fetch(`${API_BASE}/api/admin/users/${editId}`, {
          method: 'PATCH',
          headers: DEFAULT_HEADERS,
          credentials: 'include',
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.error || res.statusText);
        show('Usuário atualizado.');
      }
      setDialogOpen(false);
      fetchUsers(search);
    } catch (e) {
      if (String(e.message).includes('USERNAME_EXISTS')) return show('Usuário já existe.', 'warning');
      show(`Erro ao salvar: ${e.message}`, 'error');
    }
  }

  function openReset(u) {
    setPwUser(u);
    setNewPassword('');
    setPwOpen(true);
  }
  async function doReset() {
    try {
      if (!newPassword || newPassword.length < 8) return show('Senha precisa ter 8+ caracteres.', 'warning');
      const res = await fetch(`${API_BASE}/api/admin/users/${pwUser.id}/reset-password`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        credentials: 'include',
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || res.statusText);
      setPwOpen(false);
      show('Senha redefinida.');
    } catch (e) {
      show(`Erro ao redefinir: ${e.message}`, 'error');
    }
  }

  async function deleteUser(u) {
    if (!window.confirm(`Tem certeza que deseja remover o usuário "${u.username}" (ID ${u.id})?`)) {
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${u.id}`, {
        method: 'DELETE',
        headers: DEFAULT_HEADERS,
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || res.statusText || 'Falha ao remover');
      }
      show('Usuário removido.');
      fetchUsers(search);
    } catch (e) {
      show(`Erro ao remover: ${e.message}`, 'error');
    }
  }

  /* ============================
     Render
     ============================ */
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, textAlign: 'center' }}>
        Gerenciar Usuários
      </Typography>

      {/* Barra superior: busca/atualizar à esquerda e VOLTAR + NOVO USUÁRIO à direita */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar (usuário ou name)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 260 }}
            />
            <Button variant="text" startIcon={<RefreshIcon />} onClick={() => fetchUsers(search)} disabled={loading}>
              ATUALIZAR
            </Button>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleGoBack}>
              VOLTAR
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={onlyViewer}
            >
              NOVO USUÁRIO
            </Button>
          </Stack>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 700 }}>ID</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Usuário</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Perfil</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Setor</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Gerencia senhas?</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} hover>
                <TableCell align="center">{u.id}</TableCell>
                <TableCell align="center">{u.username}</TableCell>
                <TableCell align="center">{u.display_name || u.displayName}</TableCell>
                <TableCell align="center">{u.role === 'MASTER' ? 'SENHOR' : 'SETOR'}</TableCell>
                <TableCell align="center">
                  {(u.allowed_sector || u.allowedSector) === 'all' ? 'todo' : (u.allowed_sector || u.allowedSector)}
                </TableCell>
                <TableCell align="center">{(u.can_manage_users || u.canManageUsers) ? 'Sim' : 'Não'}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => openEdit(u)}
                    disabled={onlyViewer}
                    aria-label="Editar"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => openReset(u)}
                    disabled={onlyViewer}
                    aria-label="Redefinir senha"
                  >
                    <KeyIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => deleteUser(u)}
                    disabled={onlyViewer}
                    aria-label="Remover"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>

              </TableRow>
            ))}

            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {loading ? 'Carregando…' : 'Nenhum usuário encontrado.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId === null ? 'Novo usuário' : `Editar usuário #${editId}`}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'grid', gap: 2 }}>
          <TextField
            label="Usuário"
            value={form.username}
            onChange={e => setForm(s => ({ ...s, username: e.target.value }))}
            disabled={editId !== null}
          />
          <TextField
            label="Name para exibição"
            value={form.displayName}
            onChange={e => setForm(s => ({ ...s, displayName: e.target.value }))}
          />
          <TextField
            select
            label="Perfil"
            value={form.role}
            onChange={e => setForm(s => ({ ...s, role: e.target.value }))}
          >
            {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>

          {form.role === 'SECTOR' && (
            <TextField
              select
              label="Setor"
              value={form.allowedSector}
              onChange={e => setForm(s => ({ ...s, allowedSector: e.target.value }))}
            >
              {SECTORS.filter(s => s !== 'all').map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          )}

          {form.role === 'MASTER' && <TextField label="Setor" value="all" disabled />}

          {/* Acesso às abas */}
          <Typography sx={{ mt: 1 }} variant="subtitle2">Acesso às abas</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
            {ALL_TABS.map(t => (
              <FormControlLabel
                key={t.id}
                control={
                  <Checkbox
                    checked={form.allowedTabs.includes(t.id)}
                    onChange={(e) =>
                      setForm(s => ({
                        ...s,
                        allowedTabs: e.target.checked
                          ? Array.from(new Set([...(s.allowedTabs || []), t.id]))
                          : (s.allowedTabs || []).filter(x => x !== t.id)
                      }))
                    }
                  />
                }
                label={t.label}
              />
            ))}
          </Box>

          {editId === null && (
            <TextField
              label="Senha inicial (8+)"
              type="password"
              value={form.password}
              onChange={e => setForm(s => ({ ...s, password: e.target.value }))}
              helperText="Será guardada com proteção (hash) no servidor."
            />
          )}

          {me?.username === 'Joziel' && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.canManageUsers}
                  onChange={e => setForm(s => ({ ...s, canManageUsers: e.target.checked }))}
                />
              }
              label="Pode gerenciar usuários (ver página de senhas)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveUser} disabled={onlyViewer}>
            {editId === null ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog reset senha */}
      <Dialog open={pwOpen} onClose={() => setPwOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Redefinir senha — {pwUser?.username}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Nova senha (8+)"
            type="password"
            fullWidth
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={doReset} disabled={onlyViewer}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {node}
    </Box>
  );
}